import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, UserPlus, Shield, ShieldAlert, ShieldCheck } from 'lucide-react';

const Users = () => {
  const { API, user: currentUser } = useApp();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'editor'
  });

  useEffect(() => {
    fetchUsers();
  }, [API]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({ username: '', email: '', password: '', role: 'editor' });
    setShowDialog(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email || '',
      password: '',
      role: user.role
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Update existing user
        const updates = { ...formData };
        if (!updates.password) delete updates.password;
        
        await axios.put(`${API}/admin/users/${editingUser.id}`, updates, { withCredentials: true });
        toast.success('User updated');
      } else {
        // Create new user
        if (!formData.password) {
          toast.error('Password is required');
          return;
        }
        await axios.post(`${API}/admin/users`, formData, { withCredentials: true });
        toast.success('User created');
      }
      
      setShowDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const toggleActive = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user.id}`, {
        is_active: !user.is_active
      }, { withCredentials: true });
      setUsers(prev => prev.map(u => 
        u.id === user.id ? { ...u, is_active: !u.is_active } : u
      ));
      toast.success(`User ${user.is_active ? 'disabled' : 'enabled'}`);
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <ShieldAlert className="w-4 h-4" />;
      case 'editor': return <ShieldCheck className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'bg-brand-red text-white';
      case 'editor': return 'bg-brand-gold text-white';
      default: return 'bg-white/10 text-white/70';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-syne font-bold text-2xl text-white">User Management</h1>
          <p className="text-white/50">Manage admin users and their roles</p>
        </div>
        <Button 
          onClick={openCreateDialog}
          className="bg-brand-red hover:bg-brand-red-light"
          data-testid="add-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Role Descriptions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-brand-dark border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-red/20 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-brand-red" />
              </div>
              <div>
                <h4 className="font-semibold text-white">Admin</h4>
                <p className="text-white/40 text-xs">Full access to all features</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-brand-dark border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-brand-gold" />
              </div>
              <div>
                <h4 className="font-semibold text-white">Editor</h4>
                <p className="text-white/40 text-xs">Content & translations only</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-brand-dark border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white/50" />
              </div>
              <div>
                <h4 className="font-semibold text-white">Viewer</h4>
                <p className="text-white/40 text-xs">Read-only access</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="bg-brand-dark border-white/5">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-white/50">User</TableHead>
                <TableHead className="text-white/50">Role</TableHead>
                <TableHead className="text-white/50">Status</TableHead>
                <TableHead className="text-white/50">Last Login</TableHead>
                <TableHead className="text-white/50 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(user => (
                <TableRow 
                  key={user.id} 
                  className="border-white/5 hover:bg-white/5"
                  data-testid={`user-row-${user.id}`}
                >
                  <TableCell>
                    <div>
                      <p className="text-white font-medium flex items-center gap-2">
                        {user.username}
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="border-brand-gold text-brand-gold text-xs">
                            You
                          </Badge>
                        )}
                      </p>
                      {user.email && (
                        <p className="text-white/40 text-sm">{user.email}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getRoleBadgeClass(user.role)}>
                      {getRoleIcon(user.role)}
                      <span className="ml-1 capitalize">{user.role}</span>
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(user)}
                      disabled={user.id === currentUser?.id}
                      className={user.is_active ? 'text-green-500' : 'text-white/40'}
                    >
                      {user.is_active ? 'Active' : 'Disabled'}
                    </Button>
                  </TableCell>
                  <TableCell className="text-white/40 text-sm">
                    {formatDate(user.last_login_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(user)}
                        className="text-white/40 hover:text-white"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteUser(user.id)}
                          className="text-white/40 hover:text-brand-red"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-brand-dark border-white/10">
          <DialogHeader>
            <DialogTitle className="font-syne text-white">
              {editingUser ? 'Edit User' : 'Create User'}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              {editingUser 
                ? 'Update user information. Leave password empty to keep current.'
                : 'Create a new admin user with the specified role.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white/70">Username</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                className="bg-brand-charcoal border-white/10 text-white"
                required
                disabled={editingUser}
                data-testid="user-username"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Email (optional)</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-brand-charcoal border-white/10 text-white"
                data-testid="user-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">
                Password {editingUser && '(leave empty to keep current)'}
              </Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="bg-brand-charcoal border-white/10 text-white"
                required={!editingUser}
                minLength={6}
                data-testid="user-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger className="bg-brand-charcoal border-white/10 text-white" data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-brand-dark border-white/10">
                  <SelectItem value="admin" className="text-white">Admin</SelectItem>
                  <SelectItem value="editor" className="text-white">Editor</SelectItem>
                  <SelectItem value="viewer" className="text-white">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="border-white/10 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-brand-red hover:bg-brand-red-light"
                data-testid="save-user-btn"
              >
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
